import * as API from "@/api/apiKeys";
import * as DashboardAPI from "@/api/apiDashboard";
import * as legacyAggregate from "@/views/setting/ApiKeys/dashboard/utils/aggregate";
import { getIsAccessDisabled } from "@/utils/permissions";

// Cache TTL: short on purpose — fresh-on-tab-visit beats stale-but-cached.
const DASHBOARD_CACHE_TTL_MS = 60 * 1000;
// Legacy /api_keys/logs pagination cap; triggers the truncation banner above.
const LEGACY_MAX_RECORDS = 5000;

let activeDashboardController = null;
let activeBreakdownController = null;
let activeHealthController = null;
const dashboardCache = new Map();

// Fall back to legacy aggregation on any axios error except aborts.
function shouldFallbackToLegacy(error) {
  if (!error) return false;
  if (error.name === "CanceledError" || error.name === "AbortError")
    return false;
  const status = error?.response?.status;
  if (status === undefined) return true; // network error / no response
  if (status >= 400 && status < 600) return true;
  return false;
}

function cacheKey(action, params) {
  return `${action}|${params?.from || ""}|${params?.to || ""}|${
    params?.group_by || ""
  }|${params?.log_types || ""}`;
}

function readCache(key) {
  const entry = dashboardCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.ts > DASHBOARD_CACHE_TTL_MS) {
    dashboardCache.delete(key);
    return null;
  }
  return entry.data;
}

function writeCache(key, data) {
  dashboardCache.set(key, { ts: Date.now(), data });
}

function flushCache() {
  dashboardCache.clear();
}

const state = {
  apiKeysList: {},
  loading: false,
  // Dashboard data is normalized to the same shape from either source.
  dashboardSeries: [],
  dashboardByLogType: [],
  dashboardTotals: null,
  dashboardByKey: [],
  dashboardByEndpoint: [],
  dashboardSource: null, // 'server' | 'legacy' | null
  dashboardRawTruncated: false,
  dashboardLoading: false,
  dashboardError: null,
  dashboardRequestId: 0,
  dashboardLastFetchAt: null,
  // /usage/health; separate so its failure leaves the rest intact.
  health: null,
  healthLoading: false,
  healthError: null,
  healthRequestId: 0,
  // Endpoint scope catalog — fetched once, reused by every create/edit dialog.
  endpointGroups: [],
};

const mutations = {
  setApiKeysList(state, data) {
    state.apiKeysList = data;
  },
  setLoading(state, data) {
    state.loading = data;
  },
  setEndpointGroups(state, data) {
    state.endpointGroups = Array.isArray(data) ? data : [];
  },

  incrementDashboardRequestId(state) {
    state.dashboardRequestId += 1;
  },
  setDashboardLoading(state, value) {
    state.dashboardLoading = value;
  },
  setDashboardError(state, value) {
    state.dashboardError = value;
  },
  setDashboardData(state, payload) {
    state.dashboardSeries = payload?.series || [];
    state.dashboardByLogType = payload?.by_log_type || [];
    state.dashboardTotals = payload?.totals || null;
    state.dashboardByKey = payload?.by_key || [];
    state.dashboardByEndpoint = payload?.by_endpoint || [];
    state.dashboardSource = payload?.source || null;
    state.dashboardRawTruncated = Boolean(payload?.truncated);
    state.dashboardLastFetchAt = payload?.fetched_at || Date.now();
  },
  incrementHealthRequestId(state) {
    state.healthRequestId += 1;
  },
  setHealthLoading(state, value) {
    state.healthLoading = value;
  },
  setHealthError(state, value) {
    state.healthError = value;
  },
  setHealth(state, value) {
    state.health = value || null;
  },
  resetDashboard(state) {
    state.dashboardSeries = [];
    state.dashboardByLogType = [];
    state.dashboardTotals = null;
    state.dashboardByKey = [];
    state.dashboardByEndpoint = [];
    state.dashboardSource = null;
    state.dashboardRawTruncated = false;
    state.dashboardError = null;
    state.dashboardLastFetchAt = null;
    state.health = null;
    state.healthError = null;
  },
};

const actions = {
  async getApiKeysList(context, data) {
    try {
      if (getIsAccessDisabled(context.rootState?.user?.api_keys_access_code)) {
        return;
      }
      context.commit("setLoading", true);
      const id = this.state.user.currentId.profile_id;
      let params = {
        profile_id: id,
        page: 1,
        per_page: 25,
      };
      params = { ...params, ...data };
      const res = await API.getApiKeysList(params);
      context.commit("setApiKeysList", res?.data);
      return res?.data;
    } catch (error) {
      console.log(error);
      const code = error?.response?.data?.code;
      context.commit("user/set_api_keys_access_code", +code, {
        root: true,
      });
      throw error;
    } finally {
      context.commit("setLoading", false);
    }
  },
  async createApiKey(context, data) {
    try {
      const id = this.state.user.currentId.profile_id;
      let params = {
        profile_id: id,
        ...data,
      };
      const res = await API.createApiKey(params);
      return res?.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  },
  async updateApiKey(context, data) {
    try {
      const id = this.state.user.currentId.profile_id;
      let params = {
        profile_id: id,
        ...data,
      };
      const res = await API.updateApiKey(params);
      return res?.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  },
  async getApiKeyEndpoints(context) {
    if (context.state.endpointGroups.length)
      return context.state.endpointGroups;
    try {
      const res = await API.getApiKeyEndpoints();
      const groups = res?.data?.endpoint_groups || [];
      context.commit("setEndpointGroups", groups);
      return groups;
    } catch (err) {
      console.log(err);
      return [];
    }
  },
  async revealApiKey(context, data) {
    try {
      const id = this.state.user.currentId.profile_id;
      let params = {
        profile_id: id,
        ...data,
      };
      const res = await API.revealApiKey(params);
      return res?.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  },
  async deleteApiKey(context, data) {
    try {
      const id = this.state.user.currentId.profile_id;
      let params = {
        profile_id: id,
        ...data,
      };
      const res = await API.deleteApiKey(params);
      return res?.data;
    } catch (err) {
      console.log(err);
      throw err;
    }
  },

  // Primary: /usage/daily + /usage/breakdown. Fallback: paginated /api_keys/logs.
  async getApiDashboard(
    context,
    {
      start_date,
      end_date,
      keys,
      log_type_slugs,
      maxRecords = LEGACY_MAX_RECORDS,
    } = {}
  ) {
    if (getIsAccessDisabled(context.rootState?.user?.api_keys_access_code)) {
      return;
    }

    const key = cacheKey("dashboard", {
      from: start_date,
      to: end_date,
      group_by: Array.isArray(keys) ? keys.join(",") : "",
      log_types: Array.isArray(log_type_slugs) ? log_type_slugs.join(",") : "",
    });
    const cached = readCache(key);
    if (cached) {
      context.commit("setDashboardData", cached);
      return cached;
    }

    if (activeDashboardController) {
      activeDashboardController.abort();
    }
    const controller = new AbortController();
    activeDashboardController = controller;

    context.commit("incrementDashboardRequestId");
    const myId = context.state.dashboardRequestId;

    context.commit("setDashboardLoading", true);
    context.commit("setDashboardError", null);

    const profile_id = context.rootState.user.currentId?.profile_id;

    try {
      try {
        const [dailyRes, breakdownRes] = await Promise.all([
          DashboardAPI.getApiUsageDaily(
            { profile_id, start_date, end_date, keys, log_type_slugs },
            controller.signal
          ),
          DashboardAPI.getApiUsageBreakdown(
            { profile_id, start_date, end_date, keys, log_type_slugs },
            controller.signal
          ),
        ]);

        if (myId !== context.state.dashboardRequestId) return;

        const daily = dailyRes?.data || {};
        const breakdown = breakdownRes?.data || {};

        const payload = {
          series: Array.isArray(daily.series)
            ? daily.series.map((row) => ({
                date: row.date,
                requests: Number(row.requests || 0),
                credits: Number(row.credits || 0),
                calls: Number(row.requests || 0),
              }))
            : [],
          by_log_type: Array.isArray(daily.by_log_type)
            ? daily.by_log_type.map((entry) => ({
                slug: entry.slug,
                name: entry.name,
                totals: {
                  credits: Number(entry.totals?.credits || 0),
                  requests: Number(entry.totals?.requests || 0),
                  calls: Number(entry.totals?.requests || 0),
                },
                series: Array.isArray(entry.series)
                  ? entry.series.map((d) => ({
                      date: d.date,
                      credits: Number(d.credits || 0),
                      requests: Number(d.requests || 0),
                      calls: Number(d.requests || 0),
                    }))
                  : [],
              }))
            : [],
          totals: {
            requests: Number(daily.totals?.requests || 0),
            credits: Number(daily.totals?.credits || 0),
            calls: Number(daily.totals?.requests || 0),
          },
          // Backend sorts by credits DESC; mirror that in percent so the top row's bar reflects its credit share.
          by_key: Array.isArray(breakdown.by_key)
            ? breakdown.by_key.map((row) => {
                const calls = Number(row.requests || 0);
                const credits = Number(row.credits || 0);
                return {
                  name: row.name,
                  calls,
                  credits,
                  last_used: row.last_used || null,
                  percent: Number(row.percent_of_credits || 0),
                  percent_of_calls: Number(row.percent_of_requests || 0),
                };
              })
            : [],
          by_endpoint: Array.isArray(breakdown.by_endpoint)
            ? breakdown.by_endpoint.map((row) => {
                const calls = Number(row.requests || 0);
                const credits = Number(row.credits || 0);
                return {
                  name: row.name,
                  slug: row.slug || null,
                  calls,
                  credits,
                  cost_per_call:
                    calls > 0 ? Number(row.cost_per_call || 0) : null,
                  percent: Number(row.percent_of_credits || 0),
                  percent_of_calls: Number(row.percent_of_requests || 0),
                };
              })
            : [],
          source: "server",
          truncated: false,
          fetched_at: Date.now(),
        };

        context.commit("setDashboardData", payload);
        writeCache(key, payload);
        return payload;
      } catch (serverError) {
        if (!shouldFallbackToLegacy(serverError)) {
          throw serverError;
        }

        // Fallback: legacy /api_keys/logs paginated aggregation.
        // Same controller is reused so it can still be aborted by a newer
        // dispatch.
        const collected = [];
        let truncated = false;
        let page = 1;
        const fromTime = start_date ? new Date(start_date).getTime() : null;

        while (collected.length < maxRecords) {
          const res = await API.getApiKeyLogs(
            { profile_id, page, is_ascending_order: false },
            { signal: controller.signal }
          );
          if (myId !== context.state.dashboardRequestId) return;

          const data = res?.data;
          const logs = Array.isArray(data?.query_logs) ? data.query_logs : [];
          if (logs.length === 0) break;

          let crossedBoundary = false;
          for (const log of logs) {
            collected.push(log);
            if (fromTime !== null) {
              const t = new Date(log.create_at).getTime();
              if (!Number.isNaN(t) && t < fromTime) crossedBoundary = true;
            }
            if (collected.length >= maxRecords) {
              truncated = true;
              break;
            }
          }
          if (crossedBoundary || truncated) break;

          const pagination = data?.pagination;
          const nextPage = (pagination?.page || page) + 1;
          const totalPages =
            pagination?.total_pages ||
            (pagination?.total && pagination?.per_page
              ? Math.ceil(pagination.total / pagination.per_page)
              : null);
          if (totalPages !== null && nextPage > totalPages) break;
          page = nextPage;
        }

        const filtered = legacyAggregate.filterLogs(collected, {
          from: start_date,
          to: end_date,
          keys,
        });
        const series = legacyAggregate
          .bucketByDay(filtered, start_date, end_date)
          .map((row) => ({
            date: row.date,
            requests: row.calls,
            calls: row.calls,
            credits: 0,
          }));
        const kpis = legacyAggregate.computeKpis(
          filtered,
          start_date,
          end_date
        );
        const by_key = legacyAggregate
          .groupBy(filtered, "masked_authentication")
          .map((r) => ({
            name: r.name,
            calls: r.calls,
            credits: 0,
            last_used: null,
            percent: r.percent,
            percent_of_credits: 0,
          }));
        const by_endpoint = legacyAggregate
          .groupBy(filtered, "log_type_name")
          .map((r) => ({
            name: r.name,
            slug: null,
            calls: r.calls,
            credits: 0,
            cost_per_call: 0,
            percent: r.percent,
            percent_of_credits: 0,
          }));

        const payload = {
          series,
          totals: {
            requests: kpis.totalCalls,
            calls: kpis.totalCalls,
            credits: 0,
          },
          by_key,
          by_endpoint,
          source: "legacy",
          truncated,
          fetched_at: Date.now(),
        };
        context.commit("setDashboardData", payload);
        writeCache(key, payload);
        return payload;
      }
    } catch (error) {
      if (error?.name === "CanceledError" || error?.name === "AbortError") {
        return;
      }
      if (myId === context.state.dashboardRequestId) {
        context.commit("setDashboardError", error);
      }
      throw error;
    } finally {
      if (myId === context.state.dashboardRequestId) {
        context.commit("setDashboardLoading", false);
      }
      if (activeDashboardController === controller) {
        activeDashboardController = null;
      }
    }
  },

  async getApiHealth(context, { start_date, end_date, keys, log_type_slugs } = {}) {
    if (getIsAccessDisabled(context.rootState?.user?.api_keys_access_code)) {
      return;
    }
    const key = cacheKey("health", {
      from: start_date,
      to: end_date,
      group_by: Array.isArray(keys) ? keys.join(",") : "",
      log_types: Array.isArray(log_type_slugs) ? log_type_slugs.join(",") : "",
    });
    const cached = readCache(key);
    if (cached) {
      context.commit("setHealth", cached);
      context.commit("setHealthError", null);
      return cached;
    }

    if (activeHealthController) activeHealthController.abort();
    const controller = new AbortController();
    activeHealthController = controller;
    context.commit("incrementHealthRequestId");
    const myId = context.state.healthRequestId;
    context.commit("setHealthLoading", true);
    context.commit("setHealthError", null);

    try {
      const res = await DashboardAPI.getApiUsageHealth(
        { profile_id: context.rootState.user.currentId?.profile_id, start_date, end_date, keys, log_type_slugs },
        controller.signal
      );
      if (myId !== context.state.healthRequestId) return;
      const data = res?.data || null;
      context.commit("setHealth", data);
      writeCache(key, data);
      return data;
    } catch (error) {
      if (error?.name === "CanceledError" || error?.name === "AbortError") return;
      if (myId === context.state.healthRequestId) {
        context.commit("setHealth", null);
        context.commit("setHealthError", error);
      }
    } finally {
      if (myId === context.state.healthRequestId) context.commit("setHealthLoading", false);
      if (activeHealthController === controller) activeHealthController = null;
    }
  },

  abortDashboard() {
    if (activeHealthController) {
      activeHealthController.abort();
      activeHealthController = null;
    }
    if (activeDashboardController) {
      activeDashboardController.abort();
      activeDashboardController = null;
    }
    if (activeBreakdownController) {
      activeBreakdownController.abort();
      activeBreakdownController = null;
    }
  },

  flushDashboardCache(context) {
    flushCache();
    context.commit("resetDashboard");
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
};
